import styled from "@emotion/styled";
import { css, useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router";
import { mixHighlightColour } from "../../design/colours";
import { trpc } from "../../utils/trpc";
import { AnnotationMenu } from "../bits/AnnotationMenu";
import { BookmarkButton } from "../bits/bookmarks/BookmarkButton";
import { Chunk } from "../bits/ChunkDisplay";
import { publicationCSS } from "../bits/cssHelpers";
import { LoadingSpinner } from "../bits/loading";
import type { PageColourOptions } from "../bits/settings/defaults";
import { pageColourOptions } from "../bits/settings/defaults";
import { PageWithSettings } from "../bits/settings/PageWithSettings";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { ArticleContext } from "../contexts/WithArticle/ArticleContext";
import { SettingsContext } from "../contexts/WithSettings/SettingsContext";
import { useBookmarks } from "../hooks/useBookmarks";
import { useHighlightObserver } from "../hooks/useHighlightObserver";
import { useTextHighlight } from "../hooks/useHighlights";
import { useSearchHighlight } from "../hooks/useSearchHighlights";
import { useTitle } from "../hooks/useTitle";
import { useUser } from "../hooks/useUser";
import {
  clusterMenus,
  debounce,
  extractChapterKey,
  getAnnotationId,
  getParentElementWithId,
  getSectionFromContents,
  isElementInViewport,
} from "../utils";
import dayjs from "dayjs";
import { responsiveMargins } from "../bits/layout/cssHelpers";
import {
  useHighlightMenu,
  usePageStyles,
  useResizeScrollPreservation,
  useScrollSectionTracker,
} from "../hooks/useReaderPage";

const ArticleWrapper = styled.div<{ options?: PageColourOptions }>`
  ${({ theme }) => theme.layers.relief.accent_20.noShadow};
  ${({ options }) =>
    options &&
    `
      background-color: ${options.background};
      color: ${options.text};

      a {
        color: ${options.link_text};
      }
    `}
`;

const ArticleBody = styled.div<{ options?: PageColourOptions }>`
  max-width: ${({ theme }) => theme.layout.contentMaxWidth};
  margin: 0 auto;

  ${({ options }) => publicationCSS(options)}

  .kg-card {
    user-select: none;
  }

  .kg-embed-card iframe {
    width: 100%;
    height: 500px;
  }

  ::selection {
    background: transparent;
  }
  blockquote:has(+ h1),
  ul:has(+ h1),
  p:has(+ h1) {
    padding-bottom: 11rem;
  }
  table:has(+ h1) {
    margin-bottom: 11rem;
  }
  h1 + h1 {
    padding-top: 3rem;
  }
`;

export const Article = () => {
  const id = "article-style";
  const rangeRef = useRef<Range>(null);
  const {
    annotationsOpen,
    focusedId,
    setFocusedId,
    setAnnotationsOpen,
    didTapMenuRef,
  } = useContext(AnnotationContext);
  const { ereaderSettings } = useContext(SettingsContext);
  const { user } = useUser();

  const {
    styles,
    htmlChunks,
    chapters,
    currentChunk,
    selectSection,
    currentSection,
    contents,
    scrollDisabled,
    setScrollDisabled,
    articleSlug,
    title,
    pendingHash,
    setPendingHash,
    setScrollSnippet,
    containerRef,
    ghostPost,
    setContents,
  } = useContext(ArticleContext);

  const { portalTargets } = useBookmarks({
    contents,
    containerRef,
    htmlChunks,
  });

  const pageColourOption = useMemo(
    () =>
      ereaderSettings?.pageColour
        ? pageColourOptions[
            ereaderSettings?.pageColour as keyof typeof pageColourOptions
          ]
        : undefined,
    [ereaderSettings?.pageColour],
  );

  const {
    applyHighlight,
    applyHighlights,
    serializeRange,
    deserializeRange,
    clearHighlight,
    clearBorder,
    applyBorder,
  } = useTextHighlight(containerRef, pageColourOption?.background);

  const navigate = useNavigate();

  const theme = useTheme();
  const isLarge = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.lg}px)`,
  );

  const colour = useMemo(
    () =>
      mixHighlightColour(
        pageColourOption?.background || theme.colours.accent.light._15,
        theme.colours.highlight.apple,
      ),
    [
      pageColourOption?.background,
      theme.colours.accent.light._15,
      theme.colours.highlight.apple,
    ],
  );

  const { clearHighlight: clearHighlightForSnippet } = useSearchHighlight(
    containerRef,
    containerRef,
    colour,
  );

  useTitle(title ? `${title} | Compass` : "Article | Compass");

  const { annotations } = useContext(AnnotationContext);

  const annotationsRef = useRef(annotations);
  const focusedIdRef = useRef(focusedId);
  useEffect(() => {
    annotationsRef.current = annotations;
    focusedIdRef.current = focusedId;
  }, [annotations, focusedId]);

  const [selectedChapter, setSelectedChapter] = useState<string>();
  const [annotationPositions, setAnnotationPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});

  usePageStyles(styles, id);
  const { currentChunkRef, resizeTimerRef } = useResizeScrollPreservation(
    currentChunk,
    currentSection,
  );
  const {
    annotationSaved,
    setAnnotationSaved,
    currentHighlightMenuOpen,
    setCurrentHighlightMenuOpen,
    annotationPosition,
    setAnnotationPosition,
    currentAnnotation,
    setCurrentAnnotation,
    closeCurrentHighlightMenu,
  } = useHighlightMenu({
    clearHighlight,
    containerRef,
    htmlChunks,
    currentSection,
  });
  useScrollSectionTracker({
    id,
    slug: articleSlug,
    contents,
    currentSection,
    selectSection,
    scrollDisabled,
    resizeTimerRef,
    closeCurrentHighlightMenu,
  });

  const updateAnnotationMenuPositions = useCallback(async () => {
    if (!annotations || !containerRef.current) return;
    const { computePosition } = await import("@floating-ui/dom");
    const containerRect = containerRef.current.getBoundingClientRect();
    const rawPositions: { id: string; x: number; y: number }[] = [];
    const rawPositionsSm: { id: string; x: number; y: number }[] = [];

    for (const ann of annotations) {
      const annotationId = getAnnotationId(ann);
      const highlightEl = document.getElementById(`highlight-${annotationId}`);
      const refEl = document.getElementById(ann.range.startNodeId);
      const floatEl = document.getElementById(`ann${ann.id}`);
      const floatElSm = document.getElementById(`sm-${ann.id}`);

      const highlightTop = highlightEl
        ? highlightEl.getBoundingClientRect().top -
          containerRect.top +
          window.scrollY +
          5
        : undefined;

      if (refEl && floatEl && currentChunkRef.current) {
        const chunkContentEl = currentChunkRef.current
          .firstChild as Element | null;
        if (chunkContentEl) {
          const refRect = refEl.getBoundingClientRect();
          const chunkRect = chunkContentEl.getBoundingClientRect();
          const virtualReference = {
            getBoundingClientRect: () =>
              ({
                x: chunkRect.left,
                y: refRect.top,
                top: refRect.top,
                right: chunkRect.right,
                bottom: refRect.bottom,
                left: chunkRect.left,
                width: chunkRect.width,
                height: refRect.height,
              }) as DOMRect,
          };
          const { x, y } = await computePosition(virtualReference, floatEl, {
            placement: "right",
          });
          rawPositions.push({ id: `${ann.id}`, x, y: highlightTop ?? y });
        }
      }

      if (refEl && floatElSm) {
        const { x, y } = await computePosition(refEl, floatElSm, {
          placement: "left",
        });
        rawPositionsSm.push({ id: `sm${ann.id}`, x, y: highlightTop ?? y });
      }
    }

    const clustered = clusterMenus(rawPositions, rawPositionsSm);
    setAnnotationPositions(clustered);
  }, [annotations, containerRef, currentChunkRef]);

  const saveAnnotation = () => {
    window.getSelection()?.removeAllRanges();
    if (!currentAnnotation || !containerRef.current) return;
    const serializedRange = serializeRange(currentAnnotation);
    if (!serializedRange) return;

    createAnnotation.mutate({
      ...currentAnnotation,
      range: serializedRange,
    });
  };

  const createAnnotation = trpc.createAnnotation.useMutation({
    onSuccess: (response) => {
      if (!annotationsOpen) {
        setScrollDisabled(true);
        setAnnotationsOpen(true);
        requestAnimationFrame(() => {
          const el = document.getElementById(response.range.startNodeId);
          el?.scrollIntoView({ behavior: "instant", block: "start" });
          setScrollDisabled(false);
        });
      }
      setFocusedId(`${response.articleSlug}_${response.id}`);
      setAnnotationSaved(true);
      clearHighlight("selected");
    },
  });

  useHighlightObserver({
    annotations: annotations || [],
    containerRef,
    applyHighlight,
    applyHighlights,
    clearHighlight,
    clearBorder,
    deserializeRange,
    htmlChunks,
    annotationsOpen,
    focusedId,
    updateMenus: updateAnnotationMenuPositions,
  });

  useEffect(() => {
    window.getSelection()?.removeAllRanges();
    requestAnimationFrame(() => {
      applyHighlights(annotations || [], focusedId);
      updateAnnotationMenuPositions();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ereaderSettings]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !articleSlug) return;

    const defRegex = /^\[\[([0-9]+)\]\]:\s*/;
    const footnoteDefinitions: Record<string, string> = {};
    const defParagraphs: HTMLElement[] = [];

    container.querySelectorAll("p").forEach((p) => {
      const text = p.textContent || "";
      const match = defRegex.exec(text);
      if (match) {
        footnoteDefinitions[match[1]] = p.innerHTML.replace(
          /^\[\[[0-9]+\]\]:\s*/,
          "",
        );
        defParagraphs.push(p as HTMLElement);
      }
    });

    if (defParagraphs.length > 0) {
      const firstDef = defParagraphs[0];
      const prev = firstDef.previousElementSibling;
      if (prev && /Notes/i.test(prev.textContent || "")) {
        prev.innerHTML = "Footnotes";
      }
      defParagraphs.forEach(
        (p) => (p.innerHTML = p.innerHTML.replace(defRegex, "$1. ")),
      );
      const renameNotes = (items: typeof contents): typeof contents => {
        if (!items) {
          return items;
        }
        return items.map((item) => ({
          ...item,
          label: item.label?.replace(/Notes/i, "Footnotes") || item.label,
          children: renameNotes(item.children),
        }));
      };

      setContents((prev) => renameNotes(prev || []));

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      let node: Node | null;
      while ((node = walker.nextNode())) {
        if (/\[\[[0-9]+\]\](?!:)/.test((node as Text).nodeValue || "")) {
          textNodes.push(node as Text);
        }
      }

      textNodes.forEach((textNode) => {
        const text = textNode.nodeValue || "";
        const fragment = document.createDocumentFragment();
        const refRegex = /\[\[([0-9]+)\]\](?!:)/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = refRegex.exec(text)) !== null) {
          const num = match[1];
          const def = footnoteDefinitions[num];
          if (!def) continue;

          if (match.index > lastIndex) {
            fragment.appendChild(
              document.createTextNode(text.slice(lastIndex, match.index)),
            );
          }

          const footnoteId = `footnote-${articleSlug}-${num}`;
          const sup = document.createElement("sup");
          const button = document.createElement("button");
          button.className = "footnote-toggle";
          button.setAttribute("aria-label", "Show footnote");
          button.setAttribute("data-id", footnoteId);
          button.textContent = `[${num}]`;
          sup.appendChild(button);
          fragment.appendChild(sup);

          if (!container.querySelector(`#${CSS.escape(footnoteId)}`)) {
            const contentSpan = document.createElement("span");
            contentSpan.id = footnoteId;
            contentSpan.className = `footnote-content${ereaderSettings?.footnotes === "inline" ? "" : " hidden"}`;
            contentSpan.innerHTML = `[${num}] ${def}`;
            fragment.appendChild(contentSpan);
          }

          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        textNode.parentNode?.replaceChild(fragment, textNode);
      });
    }

    container
      .querySelectorAll<HTMLElement>(".footnote-content")
      .forEach((el) => {
        if (ereaderSettings?.footnotes === "inline") {
          el.classList.remove("hidden");
        } else {
          el.classList.add("hidden");
        }
      });

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains("footnote-toggle")) return;
      const targetId = target.getAttribute("data-id");
      if (!targetId) return;
      const footnoteEl = container.querySelector(`#${CSS.escape(targetId)}`);
      if (!footnoteEl) return;
      footnoteEl.classList.toggle("hidden");
      clearHighlight();
      clearBorder();
      applyHighlights(annotationsRef.current || [], focusedIdRef.current);
    };

    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlChunks, articleSlug, ereaderSettings?.footnotes]);

  // replace internal article links with navigate calls to avoid full page reloads.
  // We have to do this manually because the article content is rendered from raw HTML from the backend and may contain links to other articles
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("a");
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href?.startsWith("/article/")) return;
      e.preventDefault();
      navigate(href);
    };

    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlChunks, navigate]);

  useEffect(() => {
    if (!pendingHash) return;
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(pendingHash);
      if (el) {
        el.scrollIntoView({ behavior: "instant" });
        selectSection(pendingHash, undefined, true);
        setTimeout(() => {
          if (isElementInViewport(el)) {
            setScrollSnippet(undefined);
            setPendingHash(undefined);
            setScrollDisabled(false);
          } else {
            if (attempts < 20) {
              tryScroll(attempts + 1);
            } else {
              setPendingHash(undefined);
              setScrollDisabled(false);
            }
          }
        }, 100);
      } else if (attempts < 20) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      } else {
        setPendingHash(undefined);
        setScrollDisabled(false);
      }
    };
    if (htmlChunks) {
      tryScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHash, annotationsOpen, htmlChunks]);

  const onSelectEnd = useCallback(() => {
    if (!contents) return;
    const range = rangeRef.current;
    const rect = range?.getBoundingClientRect();
    const selection = window.getSelection();
    const rangeAt =
      (selection && selection.rangeCount >= 1 && selection.getRangeAt(0)) ||
      null;
    const clonedSelection = rangeAt?.cloneContents();
    const div = document.createElement("div");
    if (clonedSelection) {
      div.appendChild(clonedSelection);
    }
    // Remove any bookmark portal elements captured by the selection
    div.querySelectorAll("[data-bookmark-portal]").forEach((el) => el.remove());
    const highlightedText = div.innerHTML;

    if (
      range &&
      !range.collapsed &&
      rect &&
      selectedChapter &&
      highlightedText
    ) {
      const chapter = getSectionFromContents(contents, selectedChapter);
      setAnnotationPosition(rect);
      setCurrentAnnotation({
        articleSlug,
        highlightedText,
        range,
        sectionId: selectedChapter,
        sectionTitle: chapter?.label || "",
        noteText: "",
        userId: user?.id,
      });
      setCurrentHighlightMenuOpen(true);
    } else {
      closeCurrentHighlightMenu();
    }
  }, [
    contents,
    selectedChapter,
    closeCurrentHighlightMenu,
    setAnnotationPosition,
    setCurrentAnnotation,
    setCurrentHighlightMenuOpen,
    user,
    articleSlug,
  ]);

  const onSelectEndDebounced = useMemo(
    () => debounce(onSelectEnd, 200),
    [onSelectEnd],
  );

  const onSelectionChange = useCallback(() => {
    if (didTapMenuRef?.current) {
      setTimeout(() => closeCurrentHighlightMenu(), 100);
    } else {
      closeCurrentHighlightMenu();
    }
    if (!articleSlug || !chapters) return;
    const selected = window.getSelection();

    if (!selected || selected?.rangeCount < 1) return;

    const range = selected?.getRangeAt(0);

    if (range?.collapsed) return;

    const startNodeId = getParentElementWithId(range.startContainer);
    const endNodeId = getParentElementWithId(range.endContainer);
    const startChapterId = extractChapterKey(startNodeId || "");
    const endChapterId = extractChapterKey(endNodeId || "");
    if (startChapterId !== endChapterId) {
      window.getSelection()?.removeAllRanges();
      return;
    }

    rangeRef.current = range;

    if (range) {
      clearHighlightForSnippet();
      return applyHighlight(range, "selected");
    }
  }, [
    articleSlug,
    chapters,
    applyHighlight,
    clearHighlightForSnippet,
    closeCurrentHighlightMenu,
    didTapMenuRef,
  ]);

  const onSelectStart = useCallback(
    (event: Event) => {
      const target = event.target as Node | null;
      const startNodeId = target ? getParentElementWithId(target) : articleSlug;
      const startChapterId = extractChapterKey(startNodeId || "");
      setSelectedChapter(startChapterId || currentSection?.id || undefined);
    },
    [articleSlug, currentSection?.id],
  );

  useEffect(() => {
    document?.addEventListener("selectstart", onSelectStart);
    document?.addEventListener("selectionchange", onSelectionChange);
    document?.addEventListener("selectionchange", onSelectEndDebounced);
    document.addEventListener("mouseup", onSelectEnd);
    document.addEventListener("pointerup", onSelectEnd);

    return () => {
      document.removeEventListener("selectstart", onSelectStart);
      document?.removeEventListener("selectionchange", onSelectionChange);
      document?.removeEventListener("selectionchange", onSelectEndDebounced);
      document?.removeEventListener("mouseup", onSelectEnd);
      document.removeEventListener("pointerup", onSelectEnd);
    };
  }, [
    onSelectStart,
    onSelectionChange,
    applyHighlight,
    onSelectEndDebounced,
    onSelectEnd,
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  // for links from the search, they use the FRJ id for the section hash
  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    if (scrollTo && htmlChunks) {
      setScrollDisabled(true);
      setSearchParams(
        (prev) => {
          prev.delete("scrollTo");
          return prev;
        },
        { replace: true },
      );
      requestAnimationFrame(() => {
        const el = document.querySelector(`[frj-id="${CSS.escape(scrollTo)}"]`);
        el?.scrollIntoView({ behavior: "instant", block: "start" });
        setScrollDisabled(false);
      });
    }
  }, [searchParams, setScrollDisabled, setSearchParams, htmlChunks]);

  return (
    <>
      <ArticleWrapper options={pageColourOption}>
        {!htmlChunks ? (
          <LoadingSpinner />
        ) : (
          <ArticleBody options={pageColourOption}>
            <PageWithSettings
              id={id}
              containerRef={containerRef}
              options={pageColourOption}
              settings={ereaderSettings}
            >
              {currentHighlightMenuOpen &&
                annotationPosition &&
                containerRef.current && (
                  <AnnotationMenu
                    saveAnnotation={saveAnnotation}
                    annotationPosition={annotationPosition}
                    textContainerRef={containerRef}
                    annotationSaved={annotationSaved}
                    didTapMenuRef={didTapMenuRef}
                  />
                )}
              {ghostPost && (
                <>
                  <div
                    css={(theme) => css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-end;
                      gap: 4px;
                      padding-top: 3rem;
                      margin-bottom: -8rem;
                      width: 100%;
                      user-select: none;
                      ${responsiveMargins({ theme })}
                    `}
                  >
                    {ghostPost.publishedAt && (
                      <div>
                        {dayjs(ghostPost.publishedAt).format("DD MMM YYYY")}
                      </div>
                    )}
                    {ghostPost.authors && (
                      <div
                        css={css`
                          display: flex;
                          gap: 8px;
                        `}
                      >
                        {ghostPost.authors.map((author, index) => (
                          <span key={author.id}>
                            {author.name}
                            {index < (ghostPost.authors?.length ?? 0) - 1
                              ? ", "
                              : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    <div
                      css={css`
                        width: 100%;
                        height: 1px;
                        border-bottom: 1px solid
                          ${theme.colours.uIGrey.mid.dark_20};
                      `}
                    ></div>

                    <img
                      css={css`
                        align-self: center;
                        padding: 12px;
                      `}
                      src={ghostPost.featureImage ?? undefined}
                      alt={ghostPost.featureImageAlt ?? ""}
                    />
                  </div>
                </>
              )}
              {Object.entries(htmlChunks)
                .sort(([a], [b]) => +a - +b)
                .map(([position, chunk]) => (
                  <Chunk
                    key={position}
                    position={position}
                    html={chunk}
                    ref={currentChunk === position ? currentChunkRef : null}
                    currentChunk={currentChunk}
                    focusedId={focusedId}
                    annotations={
                      annotations?.filter((ann) => {
                        const chapterId = extractChapterKey(ann.sectionId);
                        if (!chapters || !chapterId) return false;
                        return chapters?.[chapterId] === position;
                      }) || []
                    }
                    annotationPositions={annotationPositions}
                    annotationsOpen={annotationsOpen}
                    clearHighlight={clearHighlight}
                    clearBorder={clearBorder}
                    applyBorder={applyBorder}
                    deserializeRange={deserializeRange}
                    textContainerRef={containerRef}
                    setFocusedId={setFocusedId}
                    pageColour={pageColourOption?.background}
                  />
                ))}
            </PageWithSettings>
          </ArticleBody>
        )}
      </ArticleWrapper>
      {!isLarge &&
        portalTargets.map((target) =>
          createPortal(
            <BookmarkButton
              sectionId={target.sectionId}
              sectionLabel={target.sectionLabel}
              annotationsOpen={annotationsOpen}
              articleSlug={articleSlug}
              contents={contents}
            />,
            target.container,
            target.sectionId,
          ),
        )}
    </>
  );
};
